import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  composeQueryParams,
  initialDashboardPageMetadata,
  mergeDshbPageMetadataObjects,
  paramsSerializer
} from "../../utils";
import { getKeywordByPlatformAsync } from "../content/contentSlice";

export const readAllKeywordsAsync = createAsyncThunk(
  "keyword/readAll",
  async (payload = {}, { getState }) => {
    try {
      const { metadata } = getState().keyword;
      const params = composeQueryParams(payload, metadata);
      const response = await axios.get("/api/keyword", { params, paramsSerializer });
      return { keywords: response.data };
    } catch (error) {
      console.log(error);
    }
  }
);

export const upsertKeywordAsync = createAsyncThunk(
  "keyword/upsert",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/keyword`, payload.keyword);
      return { keyword: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data.errors);
    }
  }
);

export const quickKeywordCreateAsync = createAsyncThunk(
  "keyword/quickCreate",
  async (payload, { getState, rejectWithValue, dispatch }) => {
    const { frontend } = getState();
    const platformId = payload.PlatformId || frontend.platformData.id;
    const body = {
      title: payload.title,
      PlatformId: platformId
    };
    try {
      const response = await axios.post(`/api/keyword`, body);
      await dispatch(getKeywordByPlatformAsync({ PlatformId: platformId }));
      return { keyword: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data.errors);
    }
  }
);

export const deleteKeywordAsync = createAsyncThunk("/keyword/delete", async payload => {
  await axios.delete(`/api/keyword/${payload.keyword.id}`);
  return { id: payload.keyword.id };
});

export const keywordSlice = createSlice({
  name: "keyword",
  initialState: {
    loading: false,
    error: null,
    keywords: [],
    metadata: initialDashboardPageMetadata
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllKeywordsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(readAllKeywordsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.keywords || {};
        state.keywords = items || [];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(readAllKeywordsAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(upsertKeywordAsync.pending, state => {
        state.loading = true;
      })
      .addCase(quickKeywordCreateAsync.pending, state => {
        state.loading = true;
      })
      .addCase(quickKeywordCreateAsync.fulfilled, state => {
        state.loading = false;
      })
      .addCase(quickKeywordCreateAsync.rejected, state => {
        state.loading = false;
      })
      .addCase(upsertKeywordAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.keyword[1]) {
          // insert
          if (action.payload.keyword[0]) state.keywords.unshift(action.payload.keyword[0]);
        } else {
          // update
          state.keywords = (state.keywords || []).map(keyword =>
            keyword.id === action.payload.keyword[0].id ? action.payload.keyword[0] : keyword
          );
        }
      })
      .addCase(upsertKeywordAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload || []).reduce((acc, error) => {
          acc[error.path] = `The ${error.path} field cannot be empty`;
          return acc;
        }, {});
      })
      .addCase(deleteKeywordAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteKeywordAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords = state.keywords.filter(keyword => keyword.id !== action.payload.id);
      })
      .addCase(deleteKeywordAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});
export const { clearError, clearErrors } = keywordSlice.actions;
export default keywordSlice.reducer;
